import { ComponentClass, ElementType, ReactNode } from 'react';
import { cn } from '@anytag/shared-utils';
import { Button as ButtonAnyui, type ButtonProps as ButtonPropsAnyui } from '@anyui/core';
import { Icon, IconProps } from '../Icon';
import { Tooltip, TooltipProps } from '../Tooltip';
import { CircularProgressProps } from '../Loading';

interface LoadingProps extends CircularProgressProps {
  status?: boolean;
  title?: ReactNode;
  showIcon?: boolean;
}

export type ButtonProps = Omit<ButtonPropsAnyui, 'as' | 'className' | 'loading' | 'children'> & {
  title?: ReactNode;
  prefixIcon?: IconProps;
  suffixIcon?: IconProps;
  loading?: boolean | LoadingProps;
  tooltip?: Omit<TooltipProps, 'children'>;
  asChild?: boolean;
  children?: ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  component?: ElementType;
  to?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  id?: string;
};

export const Button = ({
  title,
  tooltip,
  loading,
  disabled,
  children,
  className,
  prefixIcon,
  suffixIcon,
  asChild,
  size = 'sm',
  color = 'primary',
  variant,
  component = 'button',
  ...restProps
}: ButtonProps) => {
  const { isLoading, loadingTitle } =
    typeof loading === 'boolean'
      ? {
          isLoading: !!loading,
          loadingTitle: undefined
        }
      : {
          isLoading: !!loading?.status,
          loadingTitle: loading?.title
        };

  const customTitle = title ? (isLoading && loadingTitle) || title : '';

  return (
    <Tooltip place="top" withinPortal {...tooltip}>
      <ButtonAnyui
        isChildrenOnly={asChild}
        component={component as ComponentClass}
        loading={isLoading}
        leftSection={
          prefixIcon ? (
            <Icon
              size="16px"
              {...prefixIcon}
              data-slot="icon"
              className={cn('prefix-icon min-w-4', prefixIcon.className)}
            />
          ) : null
        }
        variant={variant}
        color={color}
        size={size}
        disabled={isLoading || disabled}
        className={cn('items-center whitespace-nowrap', className)}
        {...restProps}
      >
        {customTitle}
        {children}
      </ButtonAnyui>
    </Tooltip>
  );
};
